import {
  Breakpoints,
  Button,
  ButtonColor,
  ButtonSize,
  ButtonWidth,
  IconChevronLeft,
  Rating,
  useMediaQuery,
} from '@leland-dev/leland-ui-library';
import React, { type FC, useEffect, useState } from 'react';
import {
  type SubmitHandler,
  useForm,
  type UseFormGetValues,
  type UseFormReturn,
} from 'react-hook-form';

import { type ReviewInput } from '../../__generated-gql-types__/globalTypes';
import { getFirstErrorMessage } from '../../utils/apollo';
import { DEFAULT_ERROR_MESSAGE } from '../../utils/constants';
import { wasMutationSuccessful } from '../../utils/mutations';
import { RATE_TO_TEXT } from '../../utils/review';
import { ErrorMessage, Textarea } from '../forms/Inputs';
import CheckboxInput from '../inputs/CheckboxInput';

import {
  type ReviewFormCoachFragment,
  useReviewFormCreateReviewMutation,
} from './__generated-gql-types__/ReviewForm.generated';

const REGISTER_OPTIONS = Object.freeze({
  RATE: (getValues: UseFormGetValues<ReviewInput>) => ({
    validate: () => {
      const rate = getValues('rate');
      if (!rate || rate === 0) {
        return 'Overall rating is required';
      }
    },
  }),
});

interface ReviewFormProps {
  coach: ReviewFormCoachFragment;
  defaultValues?: ReviewInput;
  onSuccess?: () => void;
  onRequestClose: () => void;
}

export const ReviewForm: FC<ReviewFormProps> = ({
  coach,
  defaultValues,
  onSuccess,
  onRequestClose,
}) => {
  const isMd = useMediaQuery(Breakpoints.MD);

  const [createReview, { loading: createReviewInFlight }] =
    useReviewFormCreateReviewMutation();

  const formMethods = useForm<ReviewInput>({
    defaultValues: {
      ...defaultValues,
      coachUrn: coach.id,
    },
  });

  const {
    register,
    watch,
    setValue,
    getValues,
    trigger,
    handleSubmit,
    formState: { errors },
  } = formMethods;

  register('anonymous');
  register('rate', REGISTER_OPTIONS.RATE(getValues));

  const hasCategoryReview = !!watch('categoryReviews.0.categoryUrn');
  const totalSteps = hasCategoryReview ? 3 : 2;

  useEffect(() => {
    if (hasCategoryReview) {
      register('categoryReviews.0.categoryUrn');
    }
  }, [hasCategoryReview, register]);

  const watchAnonymous = watch('anonymous');
  const watchRate = watch('rate');

  const rateTextClass = 'mt-6 text-lg font-medium leading-5';

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const [currentStep, setCurrentStep] = useState<number>(1);

  const STEP_HEADINGS = [
    `How was your overall experience with ${coach.user.firstName}?`,
    'Next, rate your experience in these areas:',
    'Write your review',
  ];

  const onContinue = () => {
    setCurrentStep((prevState) => prevState + 1);
    document?.getElementById('review-modal-content')?.scroll(0, 0);
  };

  const onBack = () => {
    setCurrentStep((prevState) => prevState - 1);
    document?.getElementById('review-modal-content')?.scroll(0, 0);
  };

  const onCreateReview = async (values: ReviewInput, closeOnSave?: boolean) => {
    setErrorMessage(null);

    try {
      const res = await createReview({
        variables: {
          input: values,
        },
      });

      if (wasMutationSuccessful(res)) {
        onSuccess?.();

        if (closeOnSave) {
          onRequestClose();
        }
      } else {
        setErrorMessage(DEFAULT_ERROR_MESSAGE);
        console.warn(getFirstErrorMessage(res.errors));
      }
    } catch (error) {
      setErrorMessage(DEFAULT_ERROR_MESSAGE);
      console.warn(error);
    }
  };

  const onSubmitWrapper: SubmitHandler<ReviewInput> = async (values) => {
    if (currentStep < totalSteps) {
      onContinue();
    } else {
      await onCreateReview(values);
    }
  };

  const SAVE_AND_EXIT_BUTTON = (
    <button
      type="button"
      className={`px-4 pt-4 text-lg font-medium text-leland-gray-dark underline md:p-0 ${
        createReviewInFlight || !watchRate ? 'button--disabled' : ''
      }`}
      onClick={() => onCreateReview(getValues(), true)}
      disabled={createReviewInFlight || !watchRate}
      id="review_form--submit_now"
    >
      Submit now
    </button>
  );

  return (
    <form
      className="flex grow flex-col overflow-hidden"
      onSubmit={handleSubmit(onSubmitWrapper)}
    >
      <div
        id="review-modal-content"
        className={`flex grow flex-col ${
          currentStep === 1 ? 'items-center text-center' : ''
        } justify-start overflow-y-auto px-6 py-8`}
      >
        <p className="text-sm font-medium uppercase tracking-wider text-leland-primary">
          Step {currentStep} of {totalSteps}
        </p>
        <h2
          className={`mt-2 text-3xl font-medium leading-8 text-leland-gray-dark`}
        >
          {STEP_HEADINGS[currentStep - 1]}
        </h2>
        <div className="mt-6">
          {currentStep === 1 ? (
            <div className="flex flex-col items-center justify-center">
              <Rating
                rate={watchRate}
                hoverable
                onSaveRating={async (index) => {
                  setValue('rate', index);
                  await trigger('rate');
                }}
              />
              {watchRate > 0 ? (
                <p className={`${rateTextClass} text-leland-gray-dark`}>
                  {RATE_TO_TEXT[watchRate]}
                </p>
              ) : (
                <p className={`${rateTextClass} text-leland-gray-extra-light`}>
                  Select a star rating
                </p>
              )}
              {errors.rate?.message ? (
                <ErrorMessage errorMessage={errors.rate.message} />
              ) : null}
            </div>
          ) : null}
          {hasCategoryReview && currentStep === 2 ? (
            <>
              <Dimension
                name="knowledge"
                title="Knowledge"
                description={`How knowledgable was ${coach.user.firstName}?`}
                formMethods={formMethods}
              />
              <Dimension
                name="value"
                title="Value"
                description={`Did you feel ${coach.user.firstName} was worth their price?`}
                formMethods={formMethods}
              />
              <Dimension
                name="responsiveness"
                title="Communication"
                description={`Did ${coach.user.firstName} communicate effectively and set clear expectations?`}
                formMethods={formMethods}
              />
              <Dimension
                name="supportiveness"
                title="Supportiveness"
                description={`How supported in your goal did you feel by ${coach.user.firstName}?`}
                formMethods={formMethods}
              />
            </>
          ) : null}
          {currentStep === totalSteps ? (
            <>
              <Textarea
                label="Headline"
                subLabel="Write a short, snappy headline that will show up above your review."
                flag="Optional"
                placeholder="Headline here..."
                register={register('headline')}
                rows={1}
              />
              <Textarea
                className="mt-6"
                label="Review"
                subLabel={`Tell other Leland users about your experience working with ${coach.user.firstName}.`}
                flag="Optional"
                placeholder="Your review here..."
                register={register('comment')}
                rows={4}
              />
            </>
          ) : null}
        </div>
      </div>
      {currentStep === 1 ? (
        <div className="flex w-full items-center justify-center border-t border-leland-gray-stroke bg-leland-gray-hover p-4">
          <CheckboxInput
            labelClassName="text-lg text-leland-gray-light"
            label="Keep my review anonymous"
            isChecked={watchAnonymous}
            onToggle={() => setValue('anonymous', !watchAnonymous)}
          />
        </div>
      ) : null}
      <div className="flex w-full flex-col items-center border-t border-leland-gray-stroke p-4 md:items-end md:rounded-b-lg">
        <div
          className={`grid w-full ${
            currentStep === 1 ? 'grid-cols-1' : 'grid-cols-2'
          } items-center gap-2 md:flex`}
        >
          {currentStep > 1 ? (
            <Button
              label="Back"
              LeftIcon={IconChevronLeft}
              size={ButtonSize.LARGE}
              width={ButtonWidth.FULL_MOBILE}
              onClick={onBack}
              id="review_form--back"
            />
          ) : null}
          <div className="flex items-center justify-end space-x-4 md:grow">
            {isMd && currentStep !== totalSteps ? SAVE_AND_EXIT_BUTTON : null}
            <Button
              type="submit"
              label={currentStep === totalSteps ? 'Save and Exit' : 'Continue'}
              buttonColor={ButtonColor.PRIMARY}
              size={ButtonSize.LARGE}
              width={ButtonWidth.FULL_MOBILE}
              disabled={createReviewInFlight}
              id="review_form--continue_or_save"
            />
          </div>
        </div>
        {!isMd && currentStep !== totalSteps ? SAVE_AND_EXIT_BUTTON : null}
        {errorMessage ? (
          <div className="flex items-center justify-end">
            <ErrorMessage errorMessage={errorMessage} />
          </div>
        ) : null}
      </div>
    </form>
  );
};

interface DimensionProps {
  name: 'knowledge' | 'value' | 'responsiveness' | 'supportiveness';
  title: string;
  description: string;
  formMethods: UseFormReturn<ReviewInput>;
}

const Dimension: FC<DimensionProps> = ({
  name,
  title,
  description,
  formMethods,
}) => {
  const { register, watch, setValue, trigger } = formMethods;

  register(`categoryReviews.0.${name}`);

  const watchDimensionRate = watch(`categoryReviews.0.${name}`);

  return (
    <div className="space-y-2 border-t border-leland-gray-stroke py-6">
      <h2 className="text-xl font-medium leading-7 text-leland-gray-dark">
        {title}
      </h2>
      <p className="text-lg leading-6 text-leland-gray-light">{description}</p>
      <Rating
        rate={watchDimensionRate ?? 0}
        hoverable
        onSaveRating={async (index) => {
          setValue(`categoryReviews.0.${name}`, index);
          await trigger(`categoryReviews.0.${name}`);
        }}
      />
    </div>
  );
};
